import * as React from 'react';

import type { PageProps } from 'gatsby';
import type { HeadProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { TrialFormAu } from 'stile-au/src/components/common/TrialFormAu';
import styled from 'styled-components';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { EvidenceSection } from 'stile-shared/src/components/2024/layout/CardSection';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';
import { SEO } from 'templates/2024/SEO';

export const ExamplesSection = styled<typeof Section>(Section)`
  .content {
    padding-top: 80px !important;
    padding-bottom: 150px !important;
    @media (max-width: 768px) {
      padding-top: 30px !important;
      padding-bottom: 60px !important;
    }
  }
    h2 {
    @media (max-width: 768px) {
      text-align: center !important;
    }
  }
  .--subheader {
    margin-bottom: 80px;
    @media (max-width: 768px) {
      margin-bottom: 60px;
      text-align: center !important;
    }
  }
    
  .examples {
    grid-column: 1 / span 12;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);
    height: 536px;
    padding: 20px 0 0 0;
    gap: 80px 28px;
    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 32px 12px;
      height: 712px;
      justify-items: center;
    }

  h2 {
    grid-column: 2 / span 10 !important;
  }


  .exampleCard {
    display: flex;
    max-width: 368px;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    h6 {
      margin: 0;
      text-align: center;
      width: 275px;
      flex-shrink: 0;
      @media (max-width: 768px) {
        width: 164px;
      }
    }
    @media (max-width: 768px) {
      width: 164px;
      .gatsby-image-wrapper {
          width: 100px;
      }
    }
  }
`;

const QuoteSection = styled.div`
  background-color: var(--stile-green-2);
  margin: 0 0 0 !important;
  padding: 150px 0 150px 0;
  width: 100%;

  h2,
  p {
    color: var(--stile-grey-8);
    margin: 0 auto;
    text-align: center;
  }

  @media (max-width: 768px) {
    padding: 80px 29px 60px;
    gap: 10px;
  }
`;

const Quote = styled.h2`
  width: 762px;
  padding-bottom: 32px;

  @media (max-width: 768px) {
    width: 100%;
    padding-bottom: 25px;
  }
`;

const Quoth = styled.p`
  margin-top: 32px;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  text-align: center;
`;

const From = styled(Quoth)`
  font-weight: 400;
`;

const LeadFormSection = styled<typeof Section>(Section)`
  .column {
    width: 509px;
    grid-column: span 6;
    h2 {
      text-align: start !important;
      margin-bottom: 20px !important;
    }
  }
  p {
    padding-bottom: 12px;
    max-width: 465px;
    font-size: 16px;
    margin-bottom: 20px !important;
  }
  ul {
    list-style: disc;
  }
  ul li {
    margin-bottom: 12px;
  }
  ul li::marker {
    font-size: 0.7em;
  }

  .content {
    padding-top: 150px !important;
  }
  padding-bottom: 150px;
  @media (max-width: 768px) {
    padding-bottom: 60px !important;
    .content {
      padding-top: 60px !important;
    }
    .column {
      grid-column: span 12;
      width: auto;
    }
    .column:first-child {
      padding-bottom: 40px;
    }
    p {
      max-width: none;
    }
    .content {
      padding-top: 0 !important;
    }
    h1 {
      margin-top: 12px !important;
    }
  }
`;

const RealClassroomsSection = styled<typeof Section>(Section)`
  background: var(--stile-grey-8) !important;
  .content {
    padding-top: 80px !important;
    @media (max-width: 768px) {
      padding: 0 0 60px !important;
    }
  }
  button {
    margin-top: 32px;
    @media (max-width: 768px) {
      margin-top: 30px;
    }
  }
  .grid-layout {
    margin-top: 0 !important;
  }

  .secondary-left,
  .tertiary-right {
    display: flex;
    flex-direction: column;
    justify-content: center;
    h2 {
      text-align: left !important;
      margin: 0;
      @media (max-width: 768px) {
        margin-bottom: 20px !important;
      }
    }
    @media (max-width: 768px) {
      width: auto;
    }
  }
  .secondary-left {
    grid-column: 1 / span 5;
  }
  .tertiary-left {
    grid-column: 1 / span 6;
    .gatsby-image-wrapper {
      border-radius: 32px;
      box-shadow: 0px 43px 26px 0px rgba(0, 0, 0, 0.04), 0px 19px 19px 0px rgba(0, 0, 0, 0.06),
        0px 5px 11px 0px rgba(0, 0, 0, 0.07);
    }
  }
  .secondary-right {
    .gatsby-image-wrapper {
      border-radius: 32px;
    }
    grid-column: 7 / span 6;
  }
  .tertiary-right {
    grid-column: 8 / span 5;
  }
  @media (max-width: 768px) {
    .secondary-left,
    .secondary-right,
    .tertiary-left,
    .tertiary-right {
      grid-column: 1 / span 12;
    }
    .secondary-left {
      margin: 0 12px;
    }
    .tertiary-left {
      grid-row: 2;
      margin-top: 30px;
    }
    .secondary-right {
      padding: 30px 0 60px;
    }
  }
  .guidance {
    p {
      padding-bottom: 12px;
    }
    grid-column: span 10;
  }
`;

const AmbassadorSection = styled<typeof RealClassroomsSection>(RealClassroomsSection)`
  .content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    padding-top: 150px !important;
    margin: 0 auto !important;
    max-width: 1160px !important;
    @media (max-width: 768px) {
      padding: 25px 12px 0 !important;
      margin: 0 25px !important;
    }
  }

  .secondary-right {
    width: 100%;
    height: auto;
    display: flex;
    justify-content: center;
    align-items: center;

    .gatsby-image-wrapper,
    .gatsby-image-wrapper img {
      width: auto;
      height: auto;
      max-width: 100%;
    }
  }

  padding-bottom: 150px !important;
  @media (max-width: 768px) {
    padding-bottom: 25px !important;
    .secondary-left {
      border-bottom: 30px solid transparent;
    }
    .secondary-right {
      padding: 0 12px 0;
    }
  }
  h4 {
    font-size: 55px;
    margin: 0;
    padding-bottom: 32px;
    line-height: 60px;
    @media (max-width: 768px) {
      padding-bottom: 32px;
      color: var(--Primary-White, #fff);
      font-family: Boing;
      font-size: 34px;
      font-style: normal;
      font-weight: 500;
      line-height: 35px;
    }
  }

  h2 {
    font-size: 21px !important;
    margin: 0 !important;
  }

  p {
    padding-bottom: 16px;
    @media (max-width: 768px) {
      padding-bottom: 8px;
    }
  }
`;

const ScienceWeekLayout = styled<typeof PageLayout>(PageLayout)`
  .section {
    padding-top: 0 !important;
  }

  @media (max-width: 768px) {
    .header-wrapper {
      background-color: var(--stile-green-2);
    }
  }

  .textBlock {
    padding: 0 0 108px 0;
  }
  @media (max-width: 768px) {
    padding-bottom: 60px !important;
    .underline-20:after {
      width: 60.128px;
      height: 14.26px;
      right: 0px;
    }
    .textBlock {
      padding: 0 0 28px 0;

      .grid-layout {
        margin-top: 0 !important;
      }
    }
  }
  .hearts {
    display: inline-block;
    left: 40px;
    @media (max-width: 768px) {
      display: flex;
      justify-content: end;
      top: 30px;
      left: 0px;
    }
  }
`;

const Banner = styled<typeof Section>(Section)`
  .content {
    padding-top: 100px !important;
    padding-bottom: 0 !important;
  }

  &.textBlock {
    padding-bottom: 0 !important;
  }

  h4 {
    color: white;
    margin: 0 0 32px;
  }

  h1 {
    font-size: 60px;
    line-height: 65px;
    margin: 0 !important;
    padding: 0 !important;
  }

  button {
    @media (max-width: 768px) {
      margin-top: 32px;
    }
  }

  @media (max-width: 768px) {
    .content {
      padding-top: 60px !important;
      padding-bottom: 0 !important;
    }
    &:first-child {
      .content {
        padding-top: 17px !important;
      }
    }
    h1 {
      font-size: 38px;
      line-height: 1.1;
    }
  }
`;

const ThemeSection = styled<typeof EvidenceSection>(EvidenceSection)`
  background-color: var(--stile-green-2) !important;

  .content {
    padding-top: 50px !important;
    padding-bottom: 150px !important;
    @media (max-width: 768px) {
      padding-top: 60px !important;
      padding-bottom: 60px !important;
    }
  }
  .--jumbo {
    margin: 40px 0 0 !important;
    @media (max-width: 768px) {
      margin: 0px !important;
    }
  }
  .whats-included {
    grid-column: 1 / span 12;
    display: flex;
  }
  .gatsby-image-wrapper {
    border-radius: 32px;
  }
  .small-left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    h2 {
      text-align: left !important;
    }
    div {
      padding-left: 20px;
      align-self: flex-end;
      @media (max-width: 768px) {
        padding-left: 0px;
      }
    }
    text-align: left;
    gap: 28px;
    @media (max-width: 768px) {
      gap: 30px;
    }
    .gatsby-image-wrapper {
      aspect-ratio: 1 !important;
      width: 367px !important;
      height: 367px !important;
      @media (max-width: 768px) {
        width: 300px !important;
        height: 300px !important;
      }
    }
  }
  .big-right {
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    max-height: 706px;
    .gatsby-image-wrapper {
      min-width: 300px !important;
      min-height: 300px !important;
      @media (max-width: 768px) {
        width: 300px !important;
        height: 300px !important;
      }
    }
  }
  .small-left {
    grid-column: 2 / span 4;
  }
  .big-right {
    grid-column: 6 / span 6;
  }
  @media (max-width: 768px) {
    .small-left,
    .big-right {
      grid-column: 1 / span 12;
      align-items: center;
    }
    .small-left {
      flex-direction: column-reverse;
      padding-bottom: 20px;
      align-items: center;
    }
    .big-right {
      justify-content: center;
      align-items: center;
    }
    padding-bottom: 0 !important;
  }
`;

export function Head(props: HeadProps) {
  return (
    <SEO
      title="Science Week 2025"
      description="Celebrate National Science Week with Stile’s hands-on immersive escape room!"
      slug={props.location.pathname}
      uniqueToLocale
    />
  );
}

function ScienceWeekPage(props: PageProps) {
  const [submissionSuccess, setSubmissionSuccess] = React.useState(false);

  const onSubmissionSuccess = () => {
    setSubmissionSuccess(true);
  };

  return (
    <ScienceWeekLayout {...props}>
      <Banner
        className="textBlock"
        backgroundColor="--stile-green-2"
        headingContent={
          <>
            <h4>
              National Science Week 2025
              <br />
              has finished. See you next year!
            </h4>
            <h1 className="--jumbo">Science Week with Stile, sorted.</h1>
          </>
        }
      ></Banner>

      <ThemeSection textColor="--stile-grey-8">
        <div className="small-left">
          <StaticImage
            src="../../../../stile-shared/assets/images/science-week/Intro_small.jpg"
            alt="Explore the Universe"
            aspectRatio={1}
          />
          <div>
            <h2>Decoding the universe</h2>
            <p style={{ marginBottom: '0' }}>
              Explore this year's theme through a week filled with hands-on fun to spark your
              <strong> Year 7-10 students'</strong> curiosity.
            </p>
            <p style={{ paddingTop: '12px' }}>
              Think escape rooms, marshmallow catapults and everything you need for an unforgettable
              Science Week!
            </p>
          </div>
        </div>
        <div className="big-right">
          <StaticImage
            src="../../../../stile-shared/assets/images/science-week/Lauren Fell_square.png"
            alt="Lauren Fell"
            width={564}
            height={564}
          />
        </div>
      </ThemeSection>
      <ExamplesSection
        backgroundColor="--white"
        headingContent="What your Science Week will look like for Year 7-10 students"
        textColor="--stile-grey-8"
      >
        <div className="examples">
          <div className="exampleCard">
            <StaticImage
              src="../../../../stile-shared/assets/images/science-week/Lessons.png"
              alt="Curriculum Aligned Content"
            />
            <h6>Curriculum-aligned content</h6>
          </div>
          <div className="exampleCard">
            <StaticImage
              src="../../../../stile-shared/assets/images/science-week/Guided 2.png"
              alt="Guided Lessons"
            />
            <h6>Follow our schedule of curated lessons</h6>
          </div>
          <div className="exampleCard">
            <StaticImage
              src="../../../../stile-shared/assets/images/science-week/Hands on.png"
              alt="Hands-on Tasks"
            />
            <h6>Hands-on tasks for every type of classroom </h6>
          </div>
          <div className="exampleCard">
            <StaticImage
              src="../../../../stile-shared/assets/images/science-week/Destination Moon.png"
              alt="Destination Moon"
            />
            <h6>Challenge your students with our space themed Escape Room</h6>
          </div>
          <div className="exampleCard">
            <StaticImage
              src="../../../../stile-shared/assets/images/science-week/Colouring 1.png"
              alt="Colouring Competition"
            />
            <h6>Let creativity take off with our National Colouring Competition</h6>
          </div>
          <div className="exampleCard">
            <StaticImage
              src="../../../../stile-shared/assets/images/science-week/Terrarium.png"
              alt="Terrarium"
            />
            <h6>Join the Lunaria One global citizen science project</h6>
          </div>
        </div>
      </ExamplesSection>
      <AmbassadorSection textColor="--white">
        <div className="secondary-left">
          <h4>
            Real&nbsp;science <br />
            Real&nbsp;role&nbsp;model.
          </h4>
          <h2>Meet Lauren Fell</h2>
          <p>
            As the Director of Lunaria One – the space start-up leading Australia's first living
            biology experiment on the Moon – and Stile's Science Week Ambassador, she brings a
            unique perspective to science education.
          </p>
          <p>
            Lauren's work at Lunaria One connects students with real research and inspires the next
            generation of curious thinkers.
          </p>
          <p>
            This Science Week, she’s your go-to guide for making science unforgettable — and
            seriously fun!
          </p>
        </div>
        <div className="secondary-right">
          <StaticImage
            src="../../../../stile-shared/assets/images/science-week/Lauren Fell.png"
            alt="Workshops & events"
          />
        </div>
      </AmbassadorSection>
      <QuoteSection>
        <Quote>"That was the best science class ever."</Quote>
        <Quoth>Said by a student. Passed on by their parent.</Quoth>
        <From>We couldn't agree more.</From>
      </QuoteSection>
    </ScienceWeekLayout>
  );
}

export default ScienceWeekPage;
